/** Hand-authored multi-stroke vector study of the user-supplied koi reference.
 * Coordinates use the reference's 1448 x 1086 canvas. No raster is shown.
 * The first four strokes form the closed Fourier contour. Every other stroke
 * is traced by its own Fourier pen; no static source path is revealed.
 */
export type KoiStroke = {
  id: string;
  path: string;
  group: "body" | "head" | "dorsal" | "tail" | "lower" | "patterns";
  closed: boolean;
};
const stroke = (
  id: string,
  group: KoiStroke["group"],
  path: string,
): KoiStroke => ({ id, group, path, closed: path.endsWith("Z") });
export const koiSource: KoiStroke[] = [
  stroke(
    "body-top",
    "body",
    "M 72 681 C 133 601 200 542 291 512 C 388 480 480 456 548 450 C 658 431 795 504 900 513 C 1025 529 1130 484 1173 427",
  ),
  stroke(
    "tail-outline",
    "tail",
    "M 1173 427 C 1211 379 1209 346 1198 285 C 1181 189 1221 135 1284 89 C 1314 68 1345 53 1370 53 C 1324 78 1305 146 1336 190 C 1356 223 1333 260 1355 287 C 1374 313 1351 345 1375 376 C 1416 418 1419 447 1408 496 C 1399 531 1411 554 1420 571 C 1375 568 1353 516 1305 478 C 1262 443 1216 456 1192 497",
  ),
  stroke(
    "body-underside",
    "body",
    "M 1192 497 C 1142 605 1061 663 965 701 C 857 746 741 761 640 763 C 570 776 497 775 438 784",
  ),
  stroke(
    "body-belly",
    "body",
    "M 438 784 C 431 788 423 791 418 793 C 332 810 226 796 158 767 C 117 751 100 731 72 716 C 52 713 58 693 72 681",
  ),
  stroke(
    "dorsal",
    "dorsal",
    "M 479 461 C 523 461 536 431 559 401 C 601 345 659 278 714 258 C 742 245 706 289 721 308 C 755 328 745 358 769 382 C 789 403 816 390 840 424 C 871 467 879 463 907 473 C 921 481 929 490 934 502 C 906 495 890 515 868 510",
  ),
  stroke(
    "pectoral",
    "lower",
    "M 426 744 C 492 730 577 729 617 755 C 654 780 659 812 654 848 C 645 890 680 926 720 924 C 730 939 672 951 625 936 C 526 910 471 839 426 744 Z",
  ),
  stroke(
    "pelvic",
    "lower",
    "M 680 760 C 727 755 770 766 797 783 C 821 798 811 827 858 850 C 810 864 742 831 680 760",
  ),
  stroke(
    "rear-fin",
    "lower",
    "M 944 709 C 978 693 1013 665 1046 682 C 1085 697 1075 749 1130 767 C 1114 786 1027 766 944 709",
  ),
  stroke(
    "gill",
    "head",
    "M 399 619 C 455 652 460 711 423 748 C 408 765 389 773 371 779",
  ),
  stroke("cheek", "head", "M 361 711 C 353 763 304 788 244 784"),
  stroke("lip", "head", "M 72 681 C 91 682 108 699 117 718"),
  stroke(
    "mouth",
    "head",
    "M 68 710 C 82 701 105 733 122 749 C 107 745 81 733 70 722",
  ),
  stroke(
    "whisker-left",
    "head",
    "M 81 731 C 86 771 67 791 52 797 C 89 799 95 761 88 738",
  ),
  stroke(
    "whisker-right",
    "head",
    "M 127 734 C 145 754 139 803 172 835 C 193 854 217 864 235 855 C 206 871 168 845 155 820 C 144 795 139 765 136 751",
  ),
  stroke(
    "eye-outline",
    "head",
    "M 174 702 C 196 703 196 669 222 674 C 239 676 247 690 256 698 C 237 695 241 726 218 730 C 198 728 189 710 174 702 Z",
  ),
  stroke(
    "eye-iris",
    "head",
    "M 202 692 C 215 681 235 687 239 702 C 240 718 223 727 211 717 C 202 711 199 700 202 692 Z",
  ),
  stroke(
    "eye-pupil",
    "head",
    "M 214 695 C 224 688 235 701 228 711 C 219 721 208 707 214 695 Z",
  ),
  stroke(
    "brow",
    "head",
    "M 177 700 C 193 693 194 669 218 672 C 236 674 245 685 250 691",
  ),
  stroke(
    "head-patch",
    "patterns",
    "M 181 574 C 167 596 145 614 155 632 C 166 639 169 634 171 644 C 196 664 220 635 247 640 C 281 641 275 670 304 671 C 338 665 339 686 355 705 C 379 731 385 694 392 686 C 407 658 366 640 373 622 C 385 610 408 631 410 609 C 411 596 398 589 394 580 C 388 562 370 557 349 560 C 329 563 338 551 319 554 C 296 562 282 559 288 542 C 299 517 332 522 350 495",
  ),
  stroke(
    "middle-patch",
    "patterns",
    "M 535 456 C 521 472 491 467 483 490 C 474 513 491 520 509 514 C 530 515 517 530 536 535 C 554 542 535 554 550 569 C 560 581 576 581 581 603 C 587 622 603 623 618 623 C 641 628 638 648 657 657 C 675 667 667 686 695 691 C 723 699 731 679 739 664 C 749 650 733 635 745 627 C 757 621 768 619 762 594 C 757 569 740 553 719 541 C 698 529 725 509 700 490 C 668 469 599 440 559 449 C 548 450 539 454 535 456 Z",
  ),
  stroke(
    "rear-patch",
    "patterns",
    "M 865 509 C 877 530 911 526 927 540 C 942 547 969 546 967 566 C 966 583 947 589 937 598 C 929 611 904 610 903 629 C 899 643 913 652 892 665 C 873 670 879 686 857 692 C 824 698 830 720 811 733 C 803 743 801 744 792 747",
  ),
  stroke(
    "tail-patch",
    "patterns",
    "M 1072 511 C 1095 505 1105 486 1122 491 C 1135 499 1149 470 1159 480 C 1170 490 1143 516 1141 531 C 1136 557 1120 557 1112 578 C 1094 614 1062 611 1051 590 C 1042 577 1020 587 1017 570 C 1011 555 1035 550 1029 539 C 1025 525 1052 514 1072 511 Z",
  ),
];
const ray = (id: string, group: KoiStroke["group"], path: string) =>
  koiSource.push(stroke(id, group, path));
[
  "M 555 449 C 590 380 641 315 698 280",
  "M 582 449 C 611 398 653 350 689 320",
  "M 604 451 C 641 397 671 364 716 336",
  "M 626 456 C 658 414 694 381 730 365",
  "M 650 462 C 681 430 709 406 746 390",
  "M 677 469 C 712 443 746 418 782 407",
  "M 708 478 C 740 454 779 435 810 425",
  "M 739 486 C 772 464 802 451 830 445",
  "M 774 494 C 801 480 829 467 852 462",
  "M 808 502 C 833 490 858 480 879 475",
  "M 840 508 C 860 498 885 491 906 489",
  "M 1215 419 C 1205 294 1183 184 1328 85",
  "M 1214 433 C 1228 308 1223 235 1293 160",
  "M 1209 450 C 1249 329 1248 266 1309 211",
  "M 1204 466 C 1251 371 1263 300 1320 269",
  "M 1197 481 C 1252 404 1293 341 1336 334",
  "M 1192 496 C 1261 397 1320 373 1373 413",
  "M 1202 475 C 1272 394 1340 412 1385 465",
  "M 1202 484 C 1264 417 1344 429 1382 507",
  "M 438 745 C 490 751 573 765 627 788",
  "M 440 751 C 492 775 578 797 631 825",
  "M 444 760 C 493 800 564 828 628 859",
  "M 450 774 C 494 828 560 864 631 894",
  "M 462 793 C 500 853 572 902 639 925",
  "M 700 763 C 728 785 772 800 799 807",
  "M 710 767 C 737 802 785 827 820 840",
  "M 971 706 C 998 710 1040 722 1062 738",
  "M 986 699 C 1018 692 1045 704 1070 716",
  "M 967 716 C 1010 745 1050 755 1091 759",
].forEach((path, i) =>
  ray(`fin-ray-${i}`, i < 11 ? "dorsal" : i < 19 ? "tail" : "lower", path),
);

// A restrained row of independent scale marks keeps the pattern legible on a phone.
for (const [row, y, start, count] of [[0, 610, 385, 6], [1, 650, 420, 7], [2, 690, 455, 6]] as const) {
  for (let i = 0; i < count; i++) {
    const x = start + i * 78;
    koiSource.push(stroke(`scale-${row}-${i}`, "patterns",
      `M ${x} ${y} C ${x + 19} ${y + 7} ${x + 23} ${y + 24} ${x + 8} ${y + 34}`));
  }
}
